(() => {
  if (customElements.get('mp-menu-products')) return;
  const catalogCache = new Map();
  const needs = ['new', 'retrofit', 'extension'];
  const panelCounts = ['2', '4', '6', '8', '10', '12'];
  const entryBrands = {pro4:'anker', pro3:'anker', anker:'anker', ecoflow:'ecoflow', zendure:'zendure', hoymiles:'hoymiles', sunenergy:'sunenergy'};
  const familyOrder = ['pro4', 'pro3', 'ac2', 'zendure2400', 'sunenergy', 'hoymiles4020x', 'stream5000'];
  const ordered = (records) => records.slice().sort((a, b) => {
    const familyRank = (record) => { const rank = familyOrder.indexOf(record.family); return rank < 0 ? familyOrder.length : rank; };
    return familyRank(a) - familyRank(b) || Number(b.available) - Number(a.available)
      || Number(/500\s*w/i.test(b.title)) - Number(/500\s*w/i.test(a.title)) || a.title.localeCompare(b.title, 'de');
  });
  function uniqueModels(records) {
    const selected = new Map();
    const rank = (record) => Number(record.available) * 10 + (record.group === '4' ? 3 : record.group === '2' ? 2 : 1);
    records.forEach((record) => {
      const key = record.handle + ':' + record.model;
      if (!selected.has(key) || rank(record) > rank(selected.get(key))) selected.set(key, record);
    });
    return ordered([...selected.values()]);
  }
  async function requestPage(catalogUrl, page) {
    const url = new URL(catalogUrl, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Invalid catalog origin');
    url.searchParams.set('section_id', 'mp-header-menu-catalog');
    url.searchParams.set('page', String(page));
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Catalog request failed: ' + response.status);
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const catalog = document.querySelector('[data-mp-menu-catalog]');
    if (!catalog || catalog.dataset.incomplete === 'true' || catalog.querySelector('[data-incomplete="true"]')) {
      throw new Error('Catalog response is incomplete');
    }
    const pages = Number(catalog.dataset.pages);
    const current = Number(catalog.dataset.page);
    const total = Number(catalog.dataset.total);
    if (!Number.isInteger(pages) || pages < 1 || current !== page || !Number.isInteger(total) || total < 0) {
      throw new Error('Invalid catalog pagination');
    }
    return { catalog, pages, page: current, total };
  }

  async function fetchCollection(catalogUrl) {
    const first = await requestPage(catalogUrl, 1);
    const pages = new Array(first.pages);
    pages[0] = first;
    let nextPage = 2;
    let failure;
    const worker = async () => {
      try {
        while (!failure && nextPage <= first.pages) {
          const number = nextPage++;
          const result = await requestPage(catalogUrl, number);
          if (result.pages !== first.pages || result.total !== first.total) throw new Error('Catalog changed during loading');
          pages[number - 1] = result;
        }
      } catch (error) {
        failure = error;
        throw error;
      }
    };
    await Promise.allSettled(Array.from({ length: Math.min(3, first.pages - 1) }, worker));
    if (failure) throw failure;

    const records = [];
    const recordKeys = new Set();
    const seenProducts = new Set();
    let hasProductAccounting = false;
    pages.forEach(({ catalog }) => {
      catalog.querySelectorAll('[data-mp-menu-product-seen]').forEach((item) => {
        hasProductAccounting = true;
        if (item.dataset.handle) seenProducts.add(item.dataset.handle);
      });
      catalog.querySelectorAll('template[data-mp-menu-record]').forEach((template) => {
        const { handle, brand, family, model, role, group, groupLabel, available, inStock } = template.dataset;
        if (!handle || !needs.includes(role) || !group || !groupLabel || !template.content.querySelector('.mp-menu-product')) {
          throw new Error('Invalid catalog product record');
        }
        if (role === 'new' && !/^[1-9]\d*$/.test(group)) throw new Error('Invalid solar module count');
        const key = JSON.stringify([handle, model, role, group]);
        if (recordKeys.has(key)) return;
        recordKeys.add(key);
        const title = template.content.querySelector('.mp-menu-product__title').textContent.trim();
        records.push({ handle, brand, family, model: model || handle, title, role, group, label: groupLabel, available: available === 'true', inStock: inStock === 'true', template });
      });
    });
    if (hasProductAccounting && seenProducts.size !== first.total) throw new Error('Catalog product count is incomplete');
    return { records, seenProducts, total: first.total, pages: first.pages };
  }

  async function fetchCatalog(urls) {
    const records = [];
    const keys = new Set();
    const seenProducts = new Set();
    let pages = 0;
    // Keep at most three requests in flight, including when several collections are combined.
    for (const url of urls) {
      const collection = await fetchCollection(url);
      pages += collection.pages;
      collection.seenProducts.forEach((handle) => seenProducts.add(handle));
      collection.records.forEach((record) => {
        const key = JSON.stringify([record.handle, record.model, record.role, record.group]);
        if (keys.has(key)) return;
        keys.add(key);
        records.push(record);
      });
    }
    return { records, total: seenProducts.size, pages, sources: urls.length };
  }

  function getCatalog(urls) {
    const key = JSON.stringify(urls);
    if (!catalogCache.has(key)) {
      const promise = fetchCatalog(urls).catch((error) => {
        if (catalogCache.get(key) === promise) catalogCache.delete(key);
        throw error;
      });
      catalogCache.set(key, promise);
    }
    return catalogCache.get(key);
  }

  customElements.define('mp-menu-products', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.entry = this.entry || 'sets';
      this.entryGroups = this.entryGroups || {sets:'4'};
      this.entryButtons = [...this.querySelectorAll('[data-mp-menu-entry]')];
      this.disclosure = this.querySelector('[data-mp-menu-disclosure]');
      this.groups = this.querySelector('[data-mp-menu-groups]');
      this.grid = this.querySelector('[data-mp-menu-grid]');
      this.groupTitle = this.querySelector('[data-mp-menu-group-title]');
      this.resultTitle = this.querySelector('[data-mp-menu-result-title]');
      this.status = this.querySelector('[data-mp-menu-status]');
      this.retry = this.querySelector('[data-mp-menu-retry]');
      this.allLink = this.querySelector('[data-mp-menu-all]');
      if (!this.disclosure || !this.grid || !this.groups) return;
      this.grid.id ||= 'MpMenuGrid-' + this.dataset.instance;
      this.grid.setAttribute('role', 'region');
      this.grid.setAttribute('aria-labelledby', this.resultTitle.id);
      this.onClick = (event) => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-mp-menu-entry')) {
          if (this.entry === button.dataset.mpMenuEntry && !this.disclosure.hidden) { this.collapse(); return; }
          this.entry = button.dataset.mpMenuEntry;
          this.disclosure.hidden = false;
          this.render();
          if (!this.catalog) this.load();
          if (window.matchMedia('(max-width:1179px)').matches) {
            const behavior = window.matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth';
            requestAnimationFrame(() => this.disclosure.scrollIntoView({block:'start', behavior}));
          }
        } else if (button.hasAttribute('data-mp-menu-group')) {
          this.entryGroups[this.entry] = button.dataset.mpMenuGroup;
          this.renderSelection();
        } else if (button.hasAttribute('data-mp-menu-collapse')) this.collapse(true);
        else if (button.hasAttribute('data-mp-menu-retry')) this.load();
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && !this.disclosure.hidden) {
          event.preventDefault(); event.stopPropagation(); this.collapse(true);
        }
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.dataset.ready = String(Boolean(this.catalog));
      this.dataset.loadstate = this.catalog ? 'ready' : 'idle';
      this.render();
      if (!this.catalog) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) { this.visibilityObserver.disconnect(); this.load(); }
        });
        this.visibilityObserver.observe(this);
      }
    }
    label(key, fallback) { return this.dataset['label' + key] || fallback; }
    collapse(restoreFocus = false) {
      this.disclosure.hidden = true;
      this.dataset.expanded = 'false';
      this.entryButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (restoreFocus) this.entryButtons.find((button) => button.dataset.mpMenuEntry === this.entry)?.focus({preventScroll:true});
    }
    getGroups() {
      let records = (this.catalog?.records || []).filter((record) => record.available && record.inStock);
      if (this.entry === 'sets') return panelCounts.map((key) => ({key, label:key + ' ' + this.label('Modules','Solarmodule'), records:ordered(records.filter((record) => record.role === 'new' && record.group === key))}));
      records = records.filter((record) => record.brand === entryBrands[this.entry]);
      if (this.entry === 'pro4' || this.entry === 'pro3') records = records.filter((record) => record.family === this.entry);
      const categories = [['new',this.label('Sets','Komplettsets')],['retrofit',this.label('Storage','Einzelgeräte')],['extension',this.label('Extension','Erweiterungsakkus')]];
      return categories.filter(([key]) => !['pro4','pro3'].includes(this.entry) || key !== 'extension').map(([key,label]) => ({key,label,records:uniqueModels(records.filter((record) => record.role === key))}));
    }
    render() {
      this.dataset.expanded = String(!this.disclosure.hidden);
      this.dataset.selectedEntry = this.entry;
      this.entryButtons.forEach((button) => button.setAttribute('aria-expanded', String(!this.disclosure.hidden && button.dataset.mpMenuEntry === this.entry)));
      const entryButton = this.entryButtons.find((button) => button.dataset.mpMenuEntry === this.entry);
      this.allLink.href = entryButton.dataset.entryUrl;
      this.groupTitle.textContent = this.entry === 'sets' ? this.label('Panels','Solarmodule wählen') : entryButton.dataset.entryTitle;
      const groups = this.getGroups();
      if (!this.entryGroups[this.entry] || !groups.some((group) => group.key === this.entryGroups[this.entry])) {
        this.entryGroups[this.entry] = this.entry === 'sets' ? '4' : (groups.find((group) => group.records.length)?.key || 'new');
      }
      const fragment = document.createDocumentFragment();
      groups.forEach((group) => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'mp-hybrid-menu__group-button';
        button.dataset.mpMenuGroup = group.key; button.disabled = !this.catalog;
        button.setAttribute('aria-controls', this.grid.id);
        button.textContent = group.label;
        fragment.append(button);
      });
      this.groups.replaceChildren(fragment);
      if (this.catalog) this.renderSelection();
      else this.resultTitle.textContent = entryButton.dataset.entryTitle;
    }
    renderSelection() {
      const selected = this.entryGroups[this.entry];
      this.dataset.selectedGroup = selected;
      this.groups.querySelectorAll('[data-mp-menu-group]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mpMenuGroup === selected)));
      const group = this.getGroups().find((item) => item.key === selected);
      this.resultTitle.textContent = group?.label || '';
      const records = group?.records || [];
      const fragment = document.createDocumentFragment();
      records.forEach((record) => fragment.append(record.template.content.cloneNode(true)));
      this.grid.replaceChildren(fragment);
      this.dataset.resultCount = String(records.length);
      this.setStatus(records.length ? records.length + ' ' + this.label('Count','Produkte') : this.label('Empty','Keine passenden Produkte.'));
    }
    setStatus(message) { if (this.status.textContent !== message) this.status.textContent = message; }
    async load() {
      if (this.dataset.loadstate === 'loading' || this.catalog) return;
      this.dataset.loadstate = 'loading'; this.dataset.ready = 'false';
      this.grid.setAttribute('aria-busy','true'); this.retry.hidden = true;
      this.setStatus(this.label('Loading','Produkte werden geladen …'));
      try {
        let sources = JSON.parse(this.dataset.catalogUrls);
        if (!Array.isArray(sources) || !sources.length || sources.some((url) => typeof url !== 'string' || !url)) throw new Error('Invalid catalog sources');
        sources = [...new Set(sources.map((url) => new URL(url, window.location.origin).href))];
        this.catalog = await getCatalog(sources);
        if (!this.isConnected) return;
        this.dataset.loadstate = 'ready'; this.dataset.ready = 'true';
        this.dataset.catalogTotal = String(this.catalog.total);
        this.dataset.catalogPages = String(this.catalog.pages);
        this.dataset.catalogSources = String(this.catalog.sources);
        this.render();
      } catch (error) {
        if (!this.isConnected) return;
        this.dataset.loadstate = 'error'; this.dataset.loadError = error.message;
        this.setStatus(this.label('Error','Produkte konnten nicht geladen werden.'));
        this.retry.hidden = false;
      } finally { this.grid.setAttribute('aria-busy','false'); }
    }
    disconnectedCallback() {
      this.removeEventListener('click',this.onClick);
      this.removeEventListener('keydown',this.onKeydown);
      this.visibilityObserver?.disconnect(); this.initialized = false;
    }
  });
})();
